import React, { createContext, useContext, useCallback, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  selectActiveChatId,
  selectBuildStreamingState,
  selectBuildDefinitionText,
} from '../../aiAgentCenter/agentsChatsReducer';
import { useAgentChatInstanceOptional } from '../../aiAgentCenter/AgentChatInstanceContext';
import { ChatInstance } from '../../aiAgentCenter/ChatInstance';

interface BuildDefinitionContextType {
  isBuildDefinitionPanelOpen: boolean;
  currentBuildDefinitionId: string | null;
  isStreaming: boolean;
  openBuildDefinitionPanel: (buildDefinitionId: string) => void;
  closeBuildDefinitionPanel: () => void;
}

const BuildDefinitionContext = createContext<BuildDefinitionContextType | undefined>(undefined);

export const BuildDefinitionProvider: React.FC<{
  children: React.ReactNode;
  forceSidebarCollapse?: () => void;
}> = ({ children, forceSidebarCollapse }) => {
  const [isBuildDefinitionPanelOpen, setIsBuildDefinitionPanelOpen] = useState(false);
  const [currentBuildDefinitionId, setCurrentBuildDefinitionId] = useState<string | null>(null);

  const chatInstanceContext = useAgentChatInstanceOptional();
  const chatInstanceId = chatInstanceContext?.chatInstanceId ?? ChatInstance.agentsPage;
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const buildStreamingState = useSelector(selectBuildStreamingState(activeChatId ?? ''));
  const buildDefinitionText = useSelector(
    selectBuildDefinitionText(activeChatId ?? '', buildStreamingState?.buildDefinitionId ?? ''),
  );
  const previousActiveChatId = useRef<string | undefined>(activeChatId);
  const hasOpenedForCurrentStream = useRef(false);

  const resetState = useCallback((keepPanelClosed: boolean) => {
    if (!keepPanelClosed) {
      setIsBuildDefinitionPanelOpen(false);
    }
    setCurrentBuildDefinitionId(null);
  }, []);

  useEffect(() => {
    const chatIdChanged = previousActiveChatId.current !== activeChatId;
    const hadPreviousChat = previousActiveChatId.current !== undefined;
    const isGhostOrTempChat = activeChatId === 'TEMP_CHAT_ID' || activeChatId === 'GHOST_CHAT_ID';
    const willReset = isBuildDefinitionPanelOpen && hadPreviousChat && (chatIdChanged || isGhostOrTempChat);

    if (willReset) {
      resetState(true);
    }

    previousActiveChatId.current = activeChatId;
  }, [activeChatId, isBuildDefinitionPanelOpen, resetState]);

  useEffect(() => {
    return () => {
      if (isBuildDefinitionPanelOpen) {
        resetState(true);
      }
    };
  }, [isBuildDefinitionPanelOpen, resetState]);

  useEffect(() => {
    if (!buildStreamingState?.buildDefinitionId) {
      hasOpenedForCurrentStream.current = false;
      return;
    }
    if (buildDefinitionText.length > 0 && !hasOpenedForCurrentStream.current) {
      hasOpenedForCurrentStream.current = true;
      forceSidebarCollapse?.();
      setCurrentBuildDefinitionId(buildStreamingState.buildDefinitionId);
      setIsBuildDefinitionPanelOpen(true);
    }
  }, [buildStreamingState, buildDefinitionText.length, forceSidebarCollapse]);

  const openBuildDefinitionPanel = useCallback((buildDefinitionId: string) => {
    setCurrentBuildDefinitionId(buildDefinitionId);
    setIsBuildDefinitionPanelOpen(true);
  }, []);

  const closeBuildDefinitionPanel = useCallback(() => {
    resetState(false);
  }, [resetState]);

  const isStreaming = buildStreamingState?.buildDefinitionId === currentBuildDefinitionId;

  const value: BuildDefinitionContextType = {
    isBuildDefinitionPanelOpen,
    currentBuildDefinitionId,
    isStreaming,
    openBuildDefinitionPanel,
    closeBuildDefinitionPanel,
  };


  return (
    <BuildDefinitionContext.Provider value={value}>{children}</BuildDefinitionContext.Provider>
  );
};

export const useBuildDefinitions = () => {
  const context = useContext(BuildDefinitionContext);
  if (!context) {
    throw new Error('useBuildDefinitions must be used within a BuildDefinitionProvider');
  }
  return context;
};

export const useBuildDefinitionsOptional = () => {
  return useContext(BuildDefinitionContext);
};
